import type { BackgroundVariant, BorderVariant, TextVariant, ThemeColours } from "@qendo/theme";
import base from "./base";

const text: Record<TextVariant, string> = {
    primary: base.slate["900"],
    secondary: base.slate["700"],
    tertiary: base.slate["600"],
    quaternary: base.slate["500"],
    info: base.blue["600"],
    success: base.green["600"],
    warning: base.yellow["600"],
    danger: base.red["600"],
    selected: base.blue["600"],
    brand: base.blue["600"],
    neutral: base.slate["700"],
    "inverse-primary": base.slate["100"],
    "inverse-secondary": base.slate["300"],
    link: base.blue["600"],
    "link-pressed": base.blue["700"],
    "link-disabled": base.slate["600"],
};
const icon = text;

const border: Record<BorderVariant, string> = {
    primary: base.slate["300"],
    secondary: base.slate["200"],
    tertiary: base.slate["400"],
    quaternary: base.slate["500"],
    info: base.blue["300"],
    success: base.green["200"],
    warning: base.yellow["200"],
    danger: base.red["400"],
    selected: base.blue["600"],
    neutral: base.slate["200"],
    brand: base.blue["200"],
};

const background: Record<BackgroundVariant, string> = {
    info: base.blue["100"],
    success: base.green["100"],
    warning: base.yellow["100"],
    danger: base.red["100"],
    brand: base.blue["100"],
    primary: base.white,
    "primary-pressed": base.slate["100"],
    secondary: base.slate["100"],
    "secondary-pressed": base.slate["200"],
    tertiary: base.slate["400"],
    transparent: base.transparent,
    "gradient-start": "#CBD6FF99",
    "gradient-end": "#E2E7FF00",
};

export const theme: ThemeColours = {
    brand: border.brand,
    brandHighlight: text.brand,
    boxShadow: "#0d0d0d14",
    backdrop: "rgba(20, 30, 102, 0.5)", // base.blue["900"]/50% opacity
    text,
    icon,
    border,
    background,
    button: {
        primary: text["inverse-primary"],
        "primary-pressed": text["inverse-secondary"],
        "primary-disabled": text.tertiary,
        "primary-background": base.blue["900"],
        "primary-pressed-background": base.blue["950"],
        "primary-disabled-background": base.slate["300"],
        secondary: base.blue["800"],
        "secondary-pressed": base.blue["900"],
        "secondary-disabled": text.tertiary,
        "secondary-background": base.blue["200"],
        "secondary-pressed-background": base.blue["300"],
        "secondary-disabled-background": base.slate["300"],
        quiet: base.blue["600"],
        "quiet-pressed": base.blue["700"],
        "quiet-disabled": text.tertiary,
        "quiet-background": base.blue["0"],
        "quiet-pressed-background": base.blue["50"],
        "quiet-disabled-background": base.transparent,
        "quiet-inverse": text["inverse-primary"],
        "quiet-inverse-pressed": text["inverse-secondary"],
        "quiet-inverse-disabled": text.tertiary,
        "quiet-inverse-background": base.transparent,
        "quiet-inverse-pressed-background": base.transparent,
        "quiet-inverse-disabled-background": base.transparent,
        neutral: base.slate["800"],
        "neutral-pressed": base.slate["900"],
        "neutral-disabled": text.tertiary,
        "neutral-background": base.slate["300"],
        "neutral-pressed-background": base.slate["400"],
        "neutral-disabled-background": base.slate["50"],
        "neutral-quiet": base.slate["800"],
        "neutral-quiet-pressed": base.slate["900"],
        "neutral-quiet-disabled": text.tertiary,
        "neutral-quiet-background": base.transparent,
        "neutral-quiet-pressed-background": base.transparent,
        "neutral-quiet-disabled-background": base.transparent,
        destructive: text.danger,
        "destructive-pressed": base.red["700"],
        "destructive-disabled": text.tertiary,
        "destructive-background": base.red["100"],
        "destructive-pressed-background": base.red["300"],
        "destructive-disabled-background": base.slate["300"],
        "destructive-quiet": text.danger,
        "destructive-quiet-pressed": base.red["700"],
        "destructive-quiet-disabled": text.tertiary,
        "destructive-quiet-background": base.transparent,
        "destructive-quiet-pressed-background": base.transparent,
        "destructive-quiet-disabled-background": base.transparent,
        warning: text.warning,
        "warning-pressed": text.warning,
        "warning-disabled": text.tertiary,
        "warning-background": base.yellow["100"],
        "warning-pressed-background": base.yellow["300"],
        "warning-disabled-background": base.slate["300"],
        "warning-quiet": text.warning,
        "warning-quiet-pressed": text.warning,
        "warning-quiet-disabled": text.tertiary,
        "warning-quiet-background": base.transparent,
        "warning-quiet-pressed-background": base.transparent,
        "warning-quiet-disabled-background": base.transparent,
        success: text.success,
        "success-pressed": text.success,
        "success-disabled": text.tertiary,
        "success-background": base.green["100"],
        "success-pressed-background": base.green["300"],
        "success-disabled-background": base.slate["300"],
        "success-quiet": text.success,
        "success-quiet-pressed": text.success,
        "success-quiet-disabled": text.tertiary,
        "success-quiet-background": base.transparent,
        "success-quiet-pressed-background": base.transparent,
        "success-quiet-disabled-background": base.transparent,
    },
    toggle: {
        indicator: base.white,
        disabled: base.slate["200"],
        off: base.slate["400"],
        on: base.blue["500"],
    },
    check: {
        selector: base.blue["600"],
        background: base.blue["25"],
        disabled: text.tertiary,
    },
    toast: {
        background: base.slate["800"],
        foreground: base.slate["100"],
    },
    tracking: {
        indicator: base.blue["600"],
        selected: base.blue["600"],
        "past-period": base.blue["100"],
        info: base.blue["300"],
        current: base.blue["500"],
        prediction: base.slate["500"],
        feature: base.blue["300"],
        unselected: base.blue["200"],
        "gradient-top": base.blue["500"],
        "gradient-bottom": base.blue["100"],
    },
    input: {
        background: background.primary,
        border: border.primary,
        text: text.primary,
        placeholder: text.tertiary,
        icon: icon.secondary,
        "background-disabled": background.secondary,
        "text-disabled": text.secondary,
        "icon-disabled": icon.tertiary,
        "border-disabled": border.secondary,
        "border-focus": border.selected,
        "border-error": border.danger,
    },
    tabs: {
        background: base.blue["50"],
        selected: base.blue["500"],
    },
    reports: [
        text.brand,
        base.green["500"],
        base.pink["700"],
        base.yellow["500"],
        base.red["500"],
        base.navy["400"],
        base.slate["800"],
        base.blue["300"],
        base.green["800"],
        base.pink["400"],
    ],
};

export default theme;
